// src/pages/Login.jsx — fully theme-reactive (light = amber/parchment, dark = navy/blue)
import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import useSEO from "../hooks/useSEO";

/* ── icons ─────────────────────────────────────────────────────────── */
const EyeOn = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);
const EyeOff = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="17" height="17">
    <path fill="#EA4335" d="M12.24 10.285V14.4h6.887c-.648 2.41-2.519 4.114-5.187 4.114-3.48 0-6.301-2.822-6.301-6.301s2.822-6.301 6.301-6.301c1.556 0 2.975.568 4.073 1.505l2.91-2.91C18.995 2.54 15.823 1.5 12.24 1.5 6.44 1.5 1.74 6.2 1.74 12s4.7 10.5 10.5 10.5c5.8 0 10.5-4.7 10.5-10.5 0-.715-.074-1.41-.212-2.085L12.24 10.285z"/>
  </svg>
);
const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" width="17" height="17">
    <path fill="#1877F2" d="M22.675 0H1.325C.593 0 0 .593 0 1.325v21.351C0 23.407.593 24 1.325 24H12.82v-9.294H9.692V11.08h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.626h-3.12V24h6.116c.73 0 1.323-.593 1.323-1.325V1.325C24 .593 23.407 0 22.675 0z"/>
  </svg>
);

/* ── book spine data ────────────────────────────────────────────────── */
const SPINES = [
  { h: 82, w: 19 }, { h: 66, w: 15 }, { h: 92, w: 21 }, { h: 74, w: 17 },
  { h: 60, w: 13 }, { h: 86, w: 20 }, { h: 70, w: 16 }, { h: 78, w: 22 },
  { h: 64, w: 14 }, { h: 88, w: 19 }, { h: 72, w: 17 }, { h: 80, w: 21 },
];

/* ── friendly Firebase error map ───────────────────────────────────── */
const FB_ERRORS = {
  "auth/invalid-credential":   "Incorrect email or password.",
  "auth/wrong-password":       "Incorrect email or password.",
  "auth/user-not-found":       "No account found with this email.",
  "auth/email-already-in-use": "An account with this email already exists.",
  "auth/invalid-email":        "Please enter a valid email address.",
  "auth/weak-password":        "Password must be at least 6 characters.",
};

/* ══════════════════════════════════════════════════════════════════════ */
export default function Login() {
  const { loginWithEmail, signupWithEmail, loginWithGoogle, loginWithFacebook } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const from      = location.state?.from?.pathname || "/";

  const [mode, setMode]                     = useState("signin");
  const [email, setEmail]                   = useState("");
  const [password, setPassword]             = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw]                 = useState(false);
  const [showCpw, setShowCpw]               = useState(false);
  const [error, setError]                   = useState("");
  const [loading, setLoading]               = useState(false);

  const isSignUp = mode === "signup";

  useSEO({
    title: isSignUp ? "Create Account — Ebenezer Books" : "Sign In — Ebenezer Books",
    description: "Access your Christian library, sync streaks and bookmarks across devices.",
    keywords: "login, sign in, christian books, ebenezer books",
  });

  const switchMode = (next) => {
    setMode(next);
    setError(""); setEmail(""); setPassword("");
    setConfirmPassword(""); setShowPw(false); setShowCpw(false);
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setError("");
    if (isSignUp && password !== confirmPassword) {
      setError("Passwords do not match."); return;
    }
    setLoading(true);
    try {
      isSignUp ? await signupWithEmail(email, password) : await loginWithEmail(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(FB_ERRORS[err.code] || err.message || "Something went wrong.");
    } finally { setLoading(false); }
  };

  const handleSocial = async (fn) => {
    setError(""); setLoading(true);
    try { await fn(); navigate(from, { replace: true }); }
    catch (err) { setError(err.message || "Authentication error."); }
    finally { setLoading(false); }
  };

  /* ── render ─────────────────────────────────────────────────────── */
  return (
    <div className="lp-root">
      <style>{`
        /* ══ ROOT ══════════════════════════════════════════════════ */
        .lp-root {
          display: flex;
          align-items: flex-start; /* panels are independent heights */
          background: var(--cream);
          transition: background 0.4s ease;
        }

        /* ══ LEFT BRAND PANEL ══════════════════════════════════════ */
        .lp-brand {
          flex: 0 0 42%;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: flex-start;
          padding: 4rem 3.5rem 3rem;
          /* sticky so the panel never shifts when the right side grows */
          position: sticky;
          top: 0;
          height: calc(100vh - 64px);
          overflow: hidden;
          transition: background 0.4s ease;
        }

        /* LIGHT — warm amber/parchment */
        body.light-theme .lp-brand {
          background: linear-gradient(155deg, #3D2007 0%, #6B3A10 40%, #C8922A 100%);
        }
        body.light-theme .lp-brand::before {
          content: '';
          position: absolute; inset: 0;
          background:
            radial-gradient(ellipse 70% 50% at 90% 15%, rgba(255,222,120,0.18) 0%, transparent 65%),
            radial-gradient(ellipse 45% 55% at 10% 85%, rgba(255,180,60,0.10) 0%, transparent 65%);
          pointer-events: none;
        }
        body.light-theme .lp-brand-badge {
          background: rgba(255,255,255,0.15);
          border-color: rgba(255,255,255,0.25);
          color: rgba(255,240,200,0.95);
        }
        body.light-theme .lp-brand-title { color: #fff; }
        body.light-theme .lp-brand-title span { color: #FFE082; }
        body.light-theme .lp-brand-verse { color: rgba(255,255,255,0.62); }
        body.light-theme .lp-brand-ref   { color: rgba(255,230,150,0.75); }

        /* DARK — deep navy / blue */
        body.dark-theme .lp-brand {
          background: linear-gradient(155deg, #050d1a 0%, #0a1628 40%, #0f2040 100%);
        }
        body.dark-theme .lp-brand::before {
          content: '';
          position: absolute; inset: 0;
          background:
            radial-gradient(ellipse 60% 50% at 85% 15%, rgba(59,130,246,0.15) 0%, transparent 65%),
            radial-gradient(ellipse 40% 55% at 15% 80%, rgba(59,130,246,0.08) 0%, transparent 65%);
          pointer-events: none;
        }
        body.dark-theme .lp-brand-badge {
          background: rgba(59,130,246,0.15);
          border-color: rgba(59,130,246,0.3);
          color: #93C5FD;
        }
        body.dark-theme .lp-brand-title { color: #E2E8F0; }
        body.dark-theme .lp-brand-title span { color: #60A5FA; }
        body.dark-theme .lp-brand-verse { color: rgba(203,213,225,0.5); }
        body.dark-theme .lp-brand-ref   { color: rgba(96,165,250,0.65); }

        /* shared brand text */
        .lp-brand-badge {
          display: inline-flex; align-items: center; gap: 7px;
          border: 1px solid; border-radius: 20px;
          padding: 5px 14px; margin-bottom: 2rem;
          font-size: 0.72rem; font-weight: 700;
          letter-spacing: 0.07em; text-transform: uppercase;
          transition: all 0.4s ease;
        }
        .lp-brand-title {
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-size: clamp(1.8rem, 2.8vw, 2.7rem);
          font-weight: 700; line-height: 1.18;
          margin-bottom: 1.3rem;
          transition: color 0.4s ease;
        }
        .lp-brand-title span { transition: color 0.4s ease; }
        .lp-brand-verse {
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-size: 1.0rem; font-style: italic;
          line-height: 1.72; max-width: 300px;
          margin-bottom: 0.35rem;
          transition: color 0.4s ease;
        }
        .lp-brand-ref {
          font-size: 0.72rem; font-weight: 700;
          letter-spacing: 0.05em;
          transition: color 0.4s ease;
        }

        /* book spines */
        .lp-spines {
          position: absolute; bottom: 0; left: 0; right: 0;
          height: 100px; display: flex;
          align-items: flex-end; padding: 0 3rem; gap: 5px;
          opacity: 0.14; pointer-events: none;
        }
        body.light-theme .lp-spines { opacity: 0.22; }
        .lp-spine { border-radius: 3px 3px 0 0; flex-shrink: 0; }

        /* feature pills */
        .lp-pills {
          display: flex; flex-wrap: wrap; gap: 8px;
          margin-top: 2.25rem;
        }
        .lp-pill {
          display: flex; align-items: center; gap: 6px;
          padding: 6px 12px; border-radius: 20px;
          font-size: 0.72rem; font-weight: 600;
          transition: all 0.4s ease;
        }
        body.light-theme .lp-pill {
          background: rgba(255,255,255,0.12);
          color: rgba(255,255,255,0.85);
          border: 1px solid rgba(255,255,255,0.15);
        }
        body.dark-theme .lp-pill {
          background: rgba(59,130,246,0.1);
          color: rgba(147,197,253,0.85);
          border: 1px solid rgba(59,130,246,0.2);
        }

        /* ══ RIGHT FORM PANEL ══════════════════════════════════════ */
        .lp-form-panel {
          flex: 1;
          display: flex;
          /* flex-start so content never gets clipped: Sign In gets padding-top
             centering feel; Create Account scrolls naturally to reveal footer */
          align-items: flex-start;
          justify-content: center;
          padding: 3.5rem 2rem 4rem; /* top padding gives visual centering feel */
          transition: background 0.4s ease;
        }
        .lp-card {
          width: 100%; max-width: 420px;
          animation: fadeUp 0.45s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        /* ── confirm-password animated slide ───────────────────── */
        @keyframes lp-slide-in {
          from { max-height: 0; opacity: 0; transform: translateY(-6px); }
          to   { max-height: 110px; opacity: 1; transform: translateY(0); }
        }
        @keyframes lp-slide-out {
          from { max-height: 110px; opacity: 1; transform: translateY(0); }
          to   { max-height: 0; opacity: 0; transform: translateY(-6px); }
        }
        .lp-confirm-wrap {
          overflow: hidden;
        }
        .lp-confirm-wrap.entering {
          animation: lp-slide-in 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .lp-confirm-wrap.exiting {
          animation: lp-slide-out 0.22s ease forwards;
        }
        .lp-confirm-wrap.hidden {
          max-height: 0; opacity: 0; pointer-events: none;
        }
        .lp-confirm-wrap.visible {
          max-height: 110px; opacity: 1;
        }

        /* ── mode switcher tabs ────────────────────────────────── */
        .lp-tabs {
          display: flex; border-radius: 14px; padding: 5px;
          margin-bottom: 2rem; position: relative;
          transition: background 0.3s ease;
        }
        body.light-theme .lp-tabs {
          background: #EDE4D8;
          border: 1px solid #DDD2C4;
        }
        body.dark-theme .lp-tabs {
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.08);
        }
        .lp-tab {
          flex: 1; border: none; background: transparent;
          padding: 10px 0; border-radius: 10px;
          font-family: 'Outfit', sans-serif;
          font-size: 0.875rem; font-weight: 600;
          cursor: pointer; position: relative; z-index: 1;
          transition: all 0.25s ease;
          color: var(--muted);
        }
        .lp-tab.active {
          color: var(--ink);
          box-shadow: var(--shadow);
        }
        body.light-theme .lp-tab.active { background: #FFFFFF; }
        body.dark-theme  .lp-tab.active {
          background: rgba(255,255,255,0.09);
        }
        /* accent underline */
        .lp-tab.active::after {
          content: ''; position: absolute;
          bottom: 4px; left: 50%;
          transform: translateX(-50%);
          width: 20px; height: 2.5px;
          border-radius: 2px;
        }
        body.light-theme .lp-tab.active[data-mode="signin"]::after  { background: #C8922A; }
        body.light-theme .lp-tab.active[data-mode="signup"]::after  { background: #4A7C59; }
        body.dark-theme  .lp-tab.active[data-mode="signin"]::after  { background: #3B82F6; }
        body.dark-theme  .lp-tab.active[data-mode="signup"]::after  { background: #4D9C65; }

        /* ── header ─────────────────────────────────────────────── */
        .lp-mode-badge {
          display: inline-block;
          font-size: 0.67rem; font-weight: 700;
          letter-spacing: 0.07em; text-transform: uppercase;
          padding: 3px 10px; border-radius: 20px;
          margin-bottom: 0.6rem;
          transition: all 0.3s ease;
        }
        body.light-theme .lp-mode-badge.signin  { background: rgba(200,146,42,0.12); color: #C8922A; }
        body.light-theme .lp-mode-badge.signup  { background: rgba(74,124,89,0.12);  color: #4A7C59; }
        body.dark-theme  .lp-mode-badge.signin  { background: rgba(59,130,246,0.14); color: #60A5FA; }
        body.dark-theme  .lp-mode-badge.signup  { background: rgba(77,156,101,0.14); color: #4D9C65; }

        .lp-heading {
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-size: 1.85rem; font-weight: 700;
          color: var(--ink); margin-bottom: 0.28rem; line-height: 1.2;
          transition: color 0.3s ease;
        }
        .lp-sub {
          font-size: 0.82rem; color: var(--muted);
          margin-bottom: 1.75rem; line-height: 1.55;
          transition: color 0.3s ease;
        }

        /* ── error ──────────────────────────────────────────────── */
        .lp-error {
          display: flex; align-items: flex-start; gap: 8px;
          padding: 9px 13px; border-radius: 9px;
          font-size: 0.8rem; font-weight: 500;
          margin-bottom: 1.2rem; line-height: 1.5;
          background: rgba(220,38,38,0.08);
          border: 1px solid rgba(220,38,38,0.2);
          color: #ef4444;
        }

        /* ── inputs ─────────────────────────────────────────────── */
        .lp-label {
          display: block; font-size: 0.71rem; font-weight: 700;
          color: var(--muted); text-transform: uppercase;
          letter-spacing: 0.05em; margin-bottom: 6px;
          transition: color 0.3s ease;
        }
        .lp-field { margin-bottom: 1.1rem; }
        .lp-input-wrap { position: relative; }
        .lp-input {
          width: 100%; padding: 11px 14px;
          border-radius: 10px; outline: none;
          font-family: 'Outfit', sans-serif; font-size: 0.9rem;
          box-sizing: border-box;
          transition: border-color 0.2s, box-shadow 0.2s, background 0.3s;
        }
        body.light-theme .lp-input {
          background: #FAF7F2; color: #1A1208;
          border: 1.5px solid #DDD2C4;
        }
        body.light-theme .lp-input:focus {
          border-color: #C8922A;
          box-shadow: 0 0 0 3px rgba(200,146,42,0.15);
          background: #FFFDF8;
        }
        body.dark-theme .lp-input {
          background: rgba(255,255,255,0.05); color: #CBD5E1;
          border: 1.5px solid #1e3347;
        }
        body.dark-theme .lp-input:focus {
          border-color: #3B82F6;
          box-shadow: 0 0 0 3px rgba(59,130,246,0.18);
          background: rgba(255,255,255,0.07);
        }
        .lp-input.has-eye { padding-right: 44px; }
        .lp-eye {
          position: absolute; right: 12px; top: 50%;
          transform: translateY(-50%);
          background: none; border: none; cursor: pointer;
          color: var(--muted); display: flex; align-items: center;
          padding: 3px; transition: color 0.2s;
        }
        .lp-eye:hover { color: var(--gold); }

        /* ── primary button ─────────────────────────────────────── */
        .lp-btn {
          width: 100%; padding: 12.5px; border: none;
          border-radius: 10px;
          font-family: 'Outfit', sans-serif;
          font-size: 0.93rem; font-weight: 700;
          cursor: pointer; margin-top: 0.3rem; margin-bottom: 1.4rem;
          letter-spacing: 0.01em;
          transition: all 0.25s ease;
        }
        body.light-theme .lp-btn.signin-btn {
          background: linear-gradient(135deg, #C8922A, #9E6E18);
          color: #fff;
          box-shadow: 0 4px 16px rgba(200,146,42,0.3);
        }
        body.light-theme .lp-btn.signin-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 7px 20px rgba(200,146,42,0.4);
        }
        body.light-theme .lp-btn.signup-btn {
          background: linear-gradient(135deg, #4A7C59, #34593f);
          color: #fff;
          box-shadow: 0 4px 16px rgba(74,124,89,0.3);
        }
        body.light-theme .lp-btn.signup-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 7px 20px rgba(74,124,89,0.38);
        }
        body.dark-theme .lp-btn.signin-btn {
          background: linear-gradient(135deg, #2563EB, #1d4ed8);
          color: #fff;
          box-shadow: 0 4px 16px rgba(37,99,235,0.35);
        }
        body.dark-theme .lp-btn.signin-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 7px 20px rgba(37,99,235,0.48);
        }
        body.dark-theme .lp-btn.signup-btn {
          background: linear-gradient(135deg, #4D9C65, #3a7a50);
          color: #fff;
          box-shadow: 0 4px 16px rgba(77,156,101,0.32);
        }
        body.dark-theme .lp-btn.signup-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 7px 20px rgba(77,156,101,0.42);
        }
        .lp-btn:disabled { opacity: 0.58; cursor: not-allowed; transform: none !important; }

        /* ── divider ─────────────────────────────────────────────── */
        .lp-divider {
          display: flex; align-items: center; gap: 10px;
          color: var(--muted); font-size: 0.7rem;
          font-weight: 700; letter-spacing: 0.06em;
          margin-bottom: 1rem; transition: color 0.3s;
        }
        .lp-divider::before, .lp-divider::after {
          content: ''; flex: 1; height: 1px;
          background: var(--dust); transition: background 0.3s;
        }

        /* ── social buttons ─────────────────────────────────────── */
        .lp-social-row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 1.5rem; }
        .lp-social-btn {
          display: flex; align-items: center;
          justify-content: center; gap: 8px;
          padding: 10px 12px; border-radius: 10px;
          font-family: 'Outfit', sans-serif;
          font-size: 0.82rem; font-weight: 600;
          cursor: pointer; transition: all 0.2s ease;
        }
        body.light-theme .lp-social-btn {
          background: #FFFFFF; color: #1A1208;
          border: 1.5px solid #DDD2C4;
        }
        body.light-theme .lp-social-btn:hover:not(:disabled) {
          border-color: #C8922A; background: #FDF9F3;
          box-shadow: 0 2px 10px rgba(200,146,42,0.1);
        }
        body.dark-theme .lp-social-btn {
          background: rgba(255,255,255,0.05); color: #CBD5E1;
          border: 1.5px solid rgba(255,255,255,0.08);
        }
        body.dark-theme .lp-social-btn:hover:not(:disabled) {
          background: rgba(59,130,246,0.1);
          border-color: rgba(59,130,246,0.3);
        }
        .lp-social-btn:disabled { opacity: 0.52; cursor: not-allowed; }

        /* ── switch & guest ─────────────────────────────────────── */
        .lp-switch {
          text-align: center; font-size: 0.82rem;
          color: var(--muted); padding-top: 0.4rem;
          transition: color 0.3s;
        }
        .lp-switch-link {
          font-weight: 700; cursor: pointer;
          transition: color 0.2s;
          color: var(--gold);
        }
        .lp-switch-link:hover { text-decoration: underline; }
        .lp-guest {
          display: block; width: 100%; text-align: center;
          margin-top: 1rem; padding-top: 1rem;
          font-size: 0.82rem; font-weight: 500;
          cursor: pointer; background: none;
          border-top: 1px solid var(--dust);
          border-left: none; border-right: none; border-bottom: none;
          color: var(--muted); font-family: 'Outfit', sans-serif;
          transition: color 0.2s, border-color 0.3s;
        }
        .lp-guest:hover { color: var(--ink); }

        /* ── responsive ─────────────────────────────────────────── */
        @media (max-width: 768px) {
          .lp-brand { display: none; }
          .lp-form-panel { padding: 2rem 1.25rem; align-items: flex-start; padding-top: 2.5rem; }
        }
      `}</style>

      {/* ══ LEFT BRAND PANEL ════════════════════════════════════════ */}
      <aside className="lp-brand" aria-hidden="true">
        <div className="lp-brand-badge">📖 Ebenezer Books</div>

        <h1 className="lp-brand-title">
          Your Christian<br/>
          <span>Library,</span><br/>
          Anywhere.
        </h1>

        <p className="lp-brand-verse">
          "Hitherto hath the Lord helped us."
        </p>
        <p className="lp-brand-ref">— 1 Samuel 7:12</p>

        <div className="lp-pills">
          {["📚 Cloud Sync", "🔖 Bookmarks", "🔥 Reading Streaks", "🏅 Badges"].map(f => (
            <span key={f} className="lp-pill">{f}</span>
          ))}
        </div>

        {/* decorative book spines */}
        <div className="lp-spines">
          {SPINES.map((b, i) => (
            <div key={i} className="lp-spine" style={{
              height: b.h, width: b.w,
              background: i % 3 === 0 ? "var(--gold)" : i % 3 === 1 ? "var(--sage)" : "var(--rust)"
            }}/>
          ))}
        </div>
      </aside>

      {/* ══ RIGHT FORM PANEL ════════════════════════════════════════ */}
      <main className="lp-form-panel">
        <div className="lp-card">

          {/* Mode tabs */}
          <div className="lp-tabs" role="tablist">
            <button
              className={`lp-tab ${!isSignUp ? "active" : ""}`}
              data-mode="signin" role="tab"
              aria-selected={!isSignUp}
              onClick={() => switchMode("signin")}
            >Sign In</button>
            <button
              className={`lp-tab ${isSignUp ? "active" : ""}`}
              data-mode="signup" role="tab"
              aria-selected={isSignUp}
              onClick={() => switchMode("signup")}
            >Create Account</button>
          </div>

          {/* Header */}
          <div className={`lp-mode-badge ${isSignUp ? "signup" : "signin"}`}>
            {isSignUp ? "✦ New Account" : "✦ Welcome Back"}
          </div>
          <h2 className="lp-heading">
            {isSignUp ? "Join Ebenezer Books" : "Sign in to your library"}
          </h2>
          <p className="lp-sub">
            {isSignUp
              ? "Sync your bookmarks, streaks & progress across all your devices."
              : "Pick up right where you left off."}
          </p>

          {/* Error */}
          {error && (
            <div className="lp-error">
              <span>⚠️</span><span>{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleAuth} noValidate>
            {/* Email */}
            <div className="lp-field">
              <label className="lp-label" htmlFor="lp-email">Email Address</label>
              <input
                id="lp-email" type="email"
                className="lp-input"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="name@example.com"
                required disabled={loading}
                autoComplete={isSignUp ? "email" : "username"}
              />
            </div>

            {/* Password */}
            <div className="lp-field">
              <label className="lp-label" htmlFor="lp-pw">Password</label>
              <div className="lp-input-wrap">
                <input
                  id="lp-pw"
                  type={showPw ? "text" : "password"}
                  className="lp-input has-eye"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required disabled={loading}
                  autoComplete={isSignUp ? "new-password" : "current-password"}
                />
                <button type="button" className="lp-eye"
                  onClick={() => setShowPw(p => !p)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                >
                  {showPw ? <EyeOff/> : <EyeOn/>}
                </button>
              </div>
            </div>

            {/* Confirm password — always in DOM, animated in/out so nothing jumps */}
            <div className={`lp-confirm-wrap ${isSignUp ? "entering visible" : "exiting hidden"}`}>
              <div className="lp-field">
                <label className="lp-label" htmlFor="lp-cpw">Confirm Password</label>
                <div className="lp-input-wrap">
                  <input
                    id="lp-cpw"
                    type={showCpw ? "text" : "password"}
                    className="lp-input has-eye"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    required={isSignUp}
                    disabled={loading || !isSignUp}
                    tabIndex={isSignUp ? 0 : -1}
                    autoComplete="new-password"
                    aria-hidden={!isSignUp}
                  />
                  <button type="button" className="lp-eye"
                    onClick={() => setShowCpw(p => !p)}
                    aria-label={showCpw ? "Hide" : "Show"}
                    tabIndex={isSignUp ? 0 : -1}
                    disabled={!isSignUp}
                  >
                    {showCpw ? <EyeOff/> : <EyeOn/>}
                  </button>
                </div>
              </div>
            </div>

            <button
              id="auth-submit-btn" type="submit"
              className={`lp-btn ${isSignUp ? "signup-btn" : "signin-btn"}`}
              disabled={loading}
            >
              {loading ? "Please wait…" : isSignUp ? "Create Account" : "Sign In"}
            </button>
          </form>

          {/* Divider */}
          <div className="lp-divider">OR CONTINUE WITH</div>

          {/* Social */}
          <div className="lp-social-row">
            <button id="google-signin-btn" className="lp-social-btn"
              onClick={() => handleSocial(loginWithGoogle)} disabled={loading}>
              <GoogleIcon/> Google
            </button>
            <button id="facebook-signin-btn" className="lp-social-btn"
              onClick={() => handleSocial(loginWithFacebook)} disabled={loading}>
              <FacebookIcon/> Facebook
            </button>
          </div>

          {/* Switch mode */}
          <p className="lp-switch">
            {isSignUp ? "Already have an account? " : "Don't have an account? "}
            <span className="lp-switch-link"
              role="button" tabIndex={0}
              onClick={() => switchMode(isSignUp ? "signin" : "signup")}
              onKeyDown={e => e.key === "Enter" && switchMode(isSignUp ? "signin" : "signup")}
            >
              {isSignUp ? "Sign In" : "Create one"}
            </span>
          </p>

          {/* Guest */}
          <button id="guest-continue-btn" className="lp-guest"
            onClick={() => navigate(from, { replace: true })}>
            Continue as Guest →
          </button>

        </div>
      </main>
    </div>
  );
}
